/**
 * DutyOptionsSection ("Non-counter deployments") — Settings control for the free-text duties every counter
 * dropdown offers below the counters ("SOS DCAR", "B@M CTRL BOOTH"), and which of them get a row of their own
 * on the counter page.
 *
 * Belongs to the SESSION (utils/dutyOptions): everyone working this session ID sees and edits the same list.
 */
import { useState } from "react";
import { useSession } from "@/contexts/SessionContext";
import { useNotification } from "@/contexts/NotificationContext";
import { DUTY_FILL, DUTY_INK } from "@/utils/counterChoice";
import { NonCounterEffects } from "@/components/NonCounterEffects";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  DEFAULT_DUTY_OPTIONS, MAX_DUTY_LENGTH, normalizeDuty, saveDutyOptions, useDutyOptions, type DutyUse,
} from "@/utils/dutyOptions";

export function DutyOptionsSection({ use = {} }: { use?: Record<string, DutyUse> }) {
  const options = useDutyOptions();
  const { canEdit } = useSession();
  const { notifyError } = useNotification();
  const [draft, setDraft] = useState("");
  // What is waiting on a yes: dropping one option, or putting the whole list back to the defaults.
  const [confirm, setConfirm] = useState<{ kind: "remove"; text: string } | { kind: "reset" } | null>(null);
  const text = normalizeDuty(draft);
  const taken = options.some((o) => o.text === text);

  const save = (next: typeof options, clearRemoved = false) => {
    saveDutyOptions(next, clearRemoved).catch((e: unknown) =>
      notifyError(e instanceof Error && e.message ? e.message : "Could not save the duty list"));
  };
  const dropped = confirm?.kind === "remove"
    ? [confirm.text]
    : confirm?.kind === "reset" ? options.filter((o) => !DEFAULT_DUTY_OPTIONS.some((d) => d.text === o.text)).map((o) => o.text) : [];
  const inUse = dropped.filter((t) => use[t]);
  const people = new Set(inUse.flatMap((t) => use[t].officers)).size;
  const peopleLabel = `${people} officer${people === 1 ? "" : "s"}`;
  const add = () => {
    if (!text || taken || !canEdit) return;
    save([...options, { text, show_row: false }]);
    setDraft("");
  };

  return (
    <div style={{ fontSize: 14 }}>
      <div style={{ margin: "0 0 10px" }}>
        <NonCounterEffects />
        {!canEdit && <p style={{ margin: "6px 0 0", fontSize: 13, color: "#64748b" }}>View-only: ask someone with edit access to change the list.</p>}
      </div>
      {options.length > 0 && (
        <div style={{ display: "flex", alignItems: "flex-end", gap: 8, margin: "0 0 4px", fontSize: 12, color: "#64748b" }}>
          <span style={{ flex: 1 }}>Option</span>
          <span style={{ width: 76, textAlign: "center", lineHeight: 1.15 }}>Show in counter tab</span>
          <span style={{ width: 24 }} />
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
        {options.map((o, i) => (
          <div key={o.text} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                flex: 1, minWidth: 0, padding: "7px 10px", borderRadius: 6, fontStyle: "italic",
                background: DUTY_FILL, border: "1px solid #64748b", color: DUTY_INK, overflowWrap: "anywhere",
              }}
            >
              📝 {o.text}
            </span>
            <span style={{ width: 76, display: "flex", justifyContent: "center" }}>
              <input
                type="checkbox"
                checked={o.show_row}
                disabled={!canEdit}
                aria-label={`Show ${o.text} in the counter tab`}
                title="Show in counter tab"
                onChange={(e) => save(options.map((x, k) => (k === i ? { ...x, show_row: e.target.checked } : x)))}
                style={{ width: 22, height: 22, cursor: canEdit ? "pointer" : "not-allowed" }}
              />
            </span>
            <button
              type="button"
              disabled={!canEdit}
              onClick={() => setConfirm({ kind: "remove", text: o.text })}
              title={`Remove ${o.text}`}
              aria-label={`Remove ${o.text}`}
              style={{ width: 24, border: "none", background: "none", color: "#94a3b8", fontSize: 20, cursor: canEdit ? "pointer" : "not-allowed", padding: 0, lineHeight: 1 }}
            >
              ×
            </button>
          </div>
        ))}
        {options.length === 0 && <span style={{ color: "#94a3b8" }}>None: the dropdowns offer counters only.</span>}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input
          value={draft}
          maxLength={MAX_DUTY_LENGTH}
          disabled={!canEdit}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          placeholder="e.g. ODL, TRAINING"
          style={{ flex: "1 1 160px", minWidth: 0, padding: "8px 10px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: 16 }}
        />
        <button
          type="button"
          onClick={add}
          disabled={!canEdit || !text || taken}
          style={{
            padding: "8px 14px", borderRadius: 6, border: "none", fontWeight: 600,
            background: !canEdit || !text || taken ? "#cbd5e1" : "#2563eb", color: "white",
            cursor: !canEdit || !text || taken ? "not-allowed" : "pointer",
          }}
        >
          Add
        </button>
        <button
          type="button"
          disabled={!canEdit}
          onClick={() => setConfirm({ kind: "reset" })}
          style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1", background: "white", cursor: canEdit ? "pointer" : "not-allowed" }}
        >
          Reset
        </button>
      </div>
      {confirm && (
        <ConfirmDialog
          title={confirm.kind === "remove" ? `Remove ${confirm.text}?` : "Reset to the default list?"}
          variant="danger"
          confirmLabel={inUse.length > 0 ? `Take ${peopleLabel} off and ${confirm.kind === "remove" ? "remove" : "reset"}` : confirm.kind === "remove" ? "Remove" : "Reset"}
          message={
            <>
              <p style={{ margin: 0 }}>
                {confirm.kind === "remove"
                  ? <>It will no longer be offered in anyone's dropdown in this session.</>
                  : dropped.length > 0
                    ? <>{dropped.join(", ")} will no longer be offered in anyone's dropdown in this session.</>
                    : <>Every user of this session gets the default options back.</>}
              </p>
              {inUse.map((t) => (
                <p key={t} style={{ margin: "8px 0 0" }}>
                  <b>{use[t].officers.length} officer{use[t].officers.length === 1 ? "" : "s"}</b> {use[t].officers.length === 1 ? "is" : "are"} on {t}
                  {" "}({use[t].officers.slice(0, 4).join(", ")}{use[t].officers.length > 4 ? ` +${use[t].officers.length - 4}` : ""}).
                </p>
              ))}
              {inUse.length > 0 && (
                <p style={{ margin: "8px 0 0" }}>
                  Removing it takes them off it: that time becomes unassigned, available time (green) for you to plan.
                  One undo puts them back. Cancel to keep it.
                </p>
              )}
            </>
          }
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            if (confirm.kind === "remove") save(options.filter((o) => o.text !== confirm.text), inUse.length > 0);
            else save(DEFAULT_DUTY_OPTIONS, inUse.length > 0);
            setConfirm(null);
          }}
        />
      )}
      {taken && text && <div style={{ fontSize: 12, color: "#b45309", marginTop: 6 }}>{text} is already in the list.</div>}
    </div>
  );
}
